import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useMe } from '../auth/AuthContext';
import { AccountDialog } from '../account/AccountDialog';
import { coAllowed, coLabel, useCompanies, useCompanyId, useSwitchCompany } from '../lib/company';
import { useSetting } from '../lib/data';
import { useToast } from '../ui/Toast';
import { applyTheme, loadTheme, type ThemePref } from '../lib/theme';
import { IconAuto, IconBell, IconLogo, IconMenu, IconMoon, IconSun, MODULE_ICONS } from '../ui/icons';
import { MODS, NAV_GROUPS } from './modules';
import { NotificationsDialog, useNavBadges, useNotificationEffects, useNotifications } from './Notifications';

const THEMES: { key: ThemePref; label: string; title: string; icon: () => JSX.Element }[] = [
  { key: 'light', label: 'Sáng', title: 'Giao diện sáng', icon: IconSun },
  { key: 'dark', label: 'Tối', title: 'Giao diện tối', icon: IconMoon },
  { key: 'auto', label: 'Tự động', title: 'Theo hệ thống', icon: IconAuto },
];

export function Shell() {
  const { can, logout } = useAuth();
  const me = useMe();
  const nav = useNavigate();
  const here = useLocation().pathname.split('/')[1] || '';
  const [theme, setTheme] = useState<ThemePref>(loadTheme);
  const [account, setAccount] = useState(false);
  const [notes, setNotes] = useState(false);
  const [menu, setMenu] = useState(false);       // ngăn menu trượt ra (chỉ dùng ở màn hình hẹp)
  const { unread } = useNotifications();
  const badges = useNavBadges();
  useNotificationEffects();

  // Nhiều đơn vị: ô chọn đơn vị đang làm việc (chỉ hiện khi được làm việc với từ 2 đơn vị trở lên)
  const toast = useToast();
  const cos = useCompanies();
  const co = useCompanyId();
  const switchCo = useSwitchCompany();
  const company = useSetting('company');
  const allowed = cos.list.filter(c => coAllowed(me, c.id));
  useEffect(() => {      // đơn vị đã nhớ không còn / không được cấp → về đơn vị đầu tiên được cấp
    if (cos.isLoading) return;
    if (!cos.list.some(c => c.id === co) || !coAllowed(me, co)) switchCo(allowed[0]?.id ?? 'main');
  }, [cos.isLoading, cos.list, co]); // eslint-disable-line react-hooks/exhaustive-deps
  const logo = String(company.data?.logo || '');

  useEffect(() => { setMenu(false); }, [here]);
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu]);

  const item = (m: string) => {
    const Icon = MODULE_ICONS[m];
    const n = m === 'tasks' ? badges.mine : m === 'cal' ? badges.today : 0;
    const tip = m === 'tasks' ? `${n} việc bạn đang phụ trách` : `${n} lịch hôm nay`;
    return (
      <button key={m} type="button" onClick={() => { nav('/' + m); setMenu(false); }} aria-current={here === m ? 'page' : undefined}>
        <Icon /><span>{MODS[m].title}</span>{n > 0 && <span className="nt-n soft" title={tip}>{n}</span>}
      </button>
    );
  };

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">
          <div className="mark" aria-hidden="true"><IconLogo /></div>
          <span className="tb-title">Cổng nghiệp vụ Kế toán</span>
        </div>
        <button type="button" className="tb-menu" onClick={() => setMenu(true)} aria-label="Mở menu" aria-expanded={menu} aria-controls="sideNav">
          <IconMenu />{unread.length > 0 && <span className="nt-n" aria-hidden="true">{unread.length > 99 ? '99+' : unread.length}</span>}
        </button>
      </header>
      {menu && <div className="scrim" onClick={() => setMenu(false)} aria-hidden="true" />}
      <aside className={menu ? 'side open' : 'side'} id="sideNav">
        <div className="brand">
          <div className="mark" aria-hidden="true"><IconLogo /></div>
          <div><h1>Cổng nghiệp vụ<br />Kế toán</h1><small>Kế toán – Tài chính</small></div>
        </div>

        {allowed.length > 1 && (
          <div className="co-switch" id="coSwitch">
            {logo && <img src={logo} alt="" />}
            <div className="f"><label htmlFor="curCo">Đơn vị đang làm việc</label>
              <select id="curCo" value={co} onChange={e => { switchCo(e.target.value); toast(`Đang làm việc với ${coLabel(cos.list, e.target.value)}`); }}>
                {allowed.map(c => <option key={c.id} value={c.id}>{coLabel(cos.list, c.id)}</option>)}
              </select></div>
          </div>
        )}

        <nav className="mods" aria-label="Chung">
          <button type="button" className="nt-btn" onClick={() => { setNotes(true); setMenu(false); }}>
            <IconBell /><span>Thông báo</span>{unread.length > 0 && <span className="nt-n" aria-label={`${unread.length} thông báo chưa đọc`}>{unread.length > 99 ? '99+' : unread.length}</span>}
          </button>
          {['tasks', 'cal'].filter(can).map(item)}
        </nav>

        {NAV_GROUPS.map(g => {
          const mods = g.mods.filter(can);
          return mods.length ? (
            <div key={g.label} style={{ display: 'contents' }}>
              <div className="nav-l">{g.label}</div>
              <nav className="mods" aria-label={g.label}>{mods.map(item)}</nav>
            </div>
          ) : null;
        })}

        <div className="side-foot">
          <div className="theme-sw" role="radiogroup" aria-label="Chế độ xem">
            {THEMES.map(t => (
              <button key={t.key} type="button" role="radio" aria-checked={theme === t.key} title={t.title} onClick={() => { setTheme(t.key); applyTheme(t.key); }}>
                <t.icon /><span>{t.label}</span>
              </button>
            ))}
          </div>
          <div className="me">
            <div><b>{me.fullName || me.username}</b><small>{(me.role === 'admin' ? 'Quản trị' : 'Người dùng') + ' · ' + me.username}</small></div>
            <div className="me-act">
              <button type="button" className="btn small" onClick={() => { setAccount(true); setMenu(false); }}>Tài khoản của tôi</button>
              <button type="button" className="btn small" onClick={() => void logout()}>Đăng xuất</button>
            </div>
          </div>
          <div className="status"><span className="dot on" /><span>Đã kết nối dữ liệu dùng chung</span></div>
        </div>
      </aside>

      <main className="app" key={co}><Outlet /></main>
      <AccountDialog open={account} onClose={() => setAccount(false)} />
      <NotificationsDialog open={notes} onClose={() => setNotes(false)} />
    </div>
  );
}
